import React, { useState, useEffect, ReactElement, useRef } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import Notification from '../../Common/Notification';
import { Option } from '../../../types/common';
import * as types from '../../../constants/actionTypes';
import { actions as wizardActions } from '../../../actions/newProjectWizardActions';
import { actions as projectManagerActions } from '../../../actions/projectManagerActions';
import MultiSelectBox from '../../Common/MultiSelectBox/MultiSelectBox';
import InputText from '../../Common/InputText';
import { Project } from '../../../types/common';
import Modal61 from '../../Common/Modal/Modal61';

interface ProjectDetailsProps {
	setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
	projectData: Project;
	setProjectData: React.Dispatch<React.SetStateAction<Project>>;
	isFormValid: boolean;
}

const ProjectDetails = ({ setIsLoading, projectData, setProjectData, isFormValid }: ProjectDetailsProps): ReactElement => {
	const [pms, setPms] = useState<Array<Option>>([]);
	const [isAddNewPmModalLoading, setIsAddNewPmModalLoading] = useState<boolean>(false);
	const [isNewPmFormValid, setIsNewPmFormValid] = useState<boolean>(true);
	const [pmName, setPmName] = useState<string>('');

	const dispatch: Dispatch = useDispatch();
	const getFilters = async () => dispatch(wizardActions.loadProjectListFilters());
	const createProjectManager = async (pm) => dispatch(projectManagerActions.saveProjectManager(pm));

	const waiverModal = useRef();

	useEffect(() => {
		loadFilters();
	}, []);

	const loadFilters = async () => {
		setIsLoading(true);

		try {
			const response = await getFilters();
			if (response.type === types.NEW_PROJECT_WIZARD_DETAILS_FILTERS_SUCCESS) {
				const addNewOption = {
					id: -9999,
					name: '+ Add New Project Owner',
					styles: {
						backgroundColor: '#F7F5FF',
						color: '#46389E',
					},
				};
				const options = [...response.payload.projectManagers, addNewOption];
				setPms(options);
			} else if (response.type === types.NEW_PROJECT_WIZARD_DETAILS_FILTERS_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			setIsLoading(false);
		}
	};

	const onChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;

		// Generate project abbreviation from first letter of each word in project name
		const abbr = value ? value.split(' ').reduce((result, currentWord) => result + currentWord.charAt(0).toUpperCase(), '') : '';

		setProjectData((curProjectData) => ({
			...curProjectData,
			locationIds: [...curProjectData.locationIds],
			name: value,
			abbreviation: abbr,
		}));
	};

	const onChangePm = (id: number) => {
		if (id === -9999) {
			// User selected the add new option
			waiverModal.current.show();
		} else {
			setProjectData((curProjectData) => ({
				...curProjectData,
				locationIds: [...curProjectData.locationIds],
				projectManagerId: id,
			}));
		}
	};

	const onChangeDescription = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;

		setProjectData((curProjectData) => ({
			...curProjectData,
			locationIds: [...curProjectData.locationIds],
			description: value,
		}));
	};

	const onCreatePm = async () => {
		if (!validateCreatePmForm()) return false;

		setIsAddNewPmModalLoading(true);

		const projectManager = {
			id: 0,
			name: pmName,
			isActive: true,
		};

		try {
			const result = await createProjectManager(projectManager);

			if (result.type === types.CREATE_PROJECTMANAGER_SUCCESS) {
				Notification('success', 'Save Successful', 'Project Manager created');
				setIsAddNewPmModalLoading(false);
				waiverModal.current.hide();
				loadFilters();
			} else if (result.type === types.CREATE_PROJECTMANAGER_FAILURE) {
				Notification('error', 'An Error Occurred', result.payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		}
	};

	const validateCreatePmForm = () => {
		if (pmName.trim().length < 1) {
			setIsNewPmFormValid(false);
			return false;
		} else {
			return true;
		}
	};

	return (
		<div className='project-details-content'>
			<div className='wizard-content-title'>Get started by adding project details below.</div>

			<div className='row me-3'>
				<div className='col-6'>
					<form name='newProjectWizardStep1Form' noValidate className={isFormValid ? '' : 'was-validated'}>
						<div className='mb-3'>
							<label htmlFor='project-name' className='title'>
								Project Name
							</label>
							<InputText
								name='project-name'
								placeholder='What would you like to name your project?'
								type='text'
								value={projectData.name}
								onChange={onChangeName}
								isRequired={true}
								errorMessage='Project Name is required'
								maxLength={50}
							/>
							<small id='project-name-help' className='form-text text-muted sub-title'>
								Character Limit 50
							</small>
						</div>

						<div className='mb-3'>
							<label htmlFor='project-description' className='title'>
								Project Description
							</label>
							<InputText
								name='project-desciption'
								placeholder='Briefly describe the purpose of this project'
								type='text'
								value={projectData.description}
								onChange={onChangeDescription}
								isRequired={false}
							/>
						</div>

						<div className='mb-3'>
							<label htmlFor='projectOwnerId' className='title'>
								Project Owner
							</label>
							<MultiSelectBox
								name='projectOwnerId'
								idField='id'
								valueField='name'
								options={pms}
								selectedValues={projectData.projectManagerId === 0 ? '' : projectData.projectManagerId.toString()}
								onChange={onChangePm}
								isRequired={true}
								errorMessage='Project Owner is required'
								isMulti={false}
							/>
						</div>
					</form>
				</div>
			</div>

			<Modal61
				title='Create New Project Manager'
				ref={waiverModal}
				body={
					<form name='newProjectManagerForm' noValidate className={isNewPmFormValid ? '' : 'was-validated'}>
						<div className='mb-3'>
							<label htmlFor='project-manager-name' className='form-label'>
								Project Manager Name
							</label>
							<input
								id='project-manager-name'
								name='project-manager-name'
								type='text'
								className={`form-control ${!isNewPmFormValid ? 'is-invalid' : ''}`}
								value={pmName}
								onChange={(e) => setPmName(e.target.value)}
								required
								maxLength={100}
							/>
							<div className='invalid-feedback'>Project Manager Name is required</div>
							<small id='project-manager-name-help' className='form-text text-muted'>
								Character Limit 100
							</small>
						</div>
					</form>
				}
				primaryButtonText='Save'
				onPrimaryClick={onCreatePm}
				isLoading={isAddNewPmModalLoading}
			/>
		</div>
	);
};

export default ProjectDetails;
